import React, { useState, useEffect, useMemo } from 'react';
import { locationService, City, Country } from '../services/locations';
import { Pagination } from '../components/Pagination';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Plus, Loader2, MapPin, Edit, Trash2 } from 'lucide-react';
import { useForm } from 'react-hook-form';

interface CityFormData {
  name: string;
  countryId: number;
  latitude?: number;
  longitude?: number;
}

export const CitiesPage: React.FC = () => {
  const [allCities, setAllCities] = useState<City[]>([]);
  const [countries, setCountries] = useState<Country[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingCity, setEditingCity] = useState<City | null>(null);
  const [selectedCountryId, setSelectedCountryId] = useState<number>(0);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  // Client-side pagination
  const totalElements = allCities.length;
  const totalPages = Math.ceil(totalElements / pageSize);
  const cities = useMemo(() => {
    const start = currentPage * pageSize;
    return allCities.slice(start, start + pageSize);
  }, [allCities, currentPage, pageSize]);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<CityFormData>();

  useEffect(() => {
    loadCities();
    loadCountries();
  }, []);

  const loadCities = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await locationService.getAllCities();
      setAllCities(data);
    } catch (err: any) {
      setError(err.message || 'Failed to load cities');
    } finally {
      setLoading(false);
    }
  };

  const loadCountries = async () => {
    try {
      const data = await locationService.getAllCountries();
      setCountries(data.filter((c) => c.active));
    } catch (err: any) {
      console.error('Failed to load countries:', err);
    }
  };

  const handleOpenDialog = (city?: City) => {
    if (city) {
      setEditingCity(city);
      setSelectedCountryId(city.countryId);
      reset({
        name: city.name,
        countryId: city.countryId,
        latitude: city.latitude,
        longitude: city.longitude,
      });
    } else {
      setEditingCity(null);
      setSelectedCountryId(countries.length > 0 ? countries[0].id : 0);
      reset({});
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingCity(null);
    reset({});
  };

  const onSubmit = async (data: CityFormData) => {
    try {
      const cityData = {
        ...data,
        countryId: selectedCountryId,
      };

      if (editingCity) {
        await locationService.updateCity(editingCity.id, cityData);
      } else {
        await locationService.createCity(cityData);
      }
      await loadCities();
      handleCloseDialog();
    } catch (err: any) {
      setError(err.message || 'Failed to save city');
    }
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this city?')) {
      try {
        await locationService.deleteCity(id);
        await loadCities();
      } catch (err: any) {
        setError(err.message || 'Failed to delete city');
      }
    }
  };

  const getCountryName = (countryId: number) => {
    const country = countries.find((c) => c.id === countryId);
    return country?.name || 'Unknown';
  };

  if (loading && cities.length === 0) {
    return (
      <div className="flex h-96 items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Cities</h1>
          <p className="text-muted-foreground">Manage cities for location data</p>
        </div>
        <Button onClick={() => handleOpenDialog()}>
          <Plus className="h-4 w-4 mr-2" />
          Add City
        </Button>
      </div>

      {error && (
        <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4 text-destructive">
          <p className="font-medium">Error</p>
          <p className="text-sm">{error}</p>
        </div>
      )}

      <Card>
        <CardHeader>
          <CardTitle>All Cities</CardTitle>
          <CardDescription>List of all cities in the system</CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Country</TableHead>
                <TableHead>Coordinates</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {cities.map((city) => (
                <TableRow key={city.id}>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <MapPin className="h-4 w-4 text-muted-foreground" />
                      <span className="font-medium">{city.name}</span>
                    </div>
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {city.countryName || getCountryName(city.countryId)}
                  </TableCell>
                  <TableCell className="text-muted-foreground text-sm">
                    {city.latitude && city.longitude
                      ? `${city.latitude.toFixed(4)}, ${city.longitude.toFixed(4)}`
                      : '-'}
                  </TableCell>
                  <TableCell>
                    <Badge variant={city.active ? 'default' : 'secondary'}>
                      {city.active ? 'Active' : 'Inactive'}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-2">
                      <Button variant="ghost" size="sm" onClick={() => handleOpenDialog(city)}>
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDelete(city.id)}>
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {cities.length === 0 && (
            <p className="text-center text-sm text-muted-foreground py-8">No cities found</p>
          )}

          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalElements={totalElements}
            pageSize={pageSize}
            onPageChange={setCurrentPage}
            onPageSizeChange={(size) => {
              setPageSize(size);
              setCurrentPage(0);
            }}
            itemName="cities"
          />
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={openDialog} onOpenChange={setOpenDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingCity ? 'Edit City' : 'Create City'}</DialogTitle>
            <DialogDescription>
              {editingCity ? 'Update city details' : 'Add a new city'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div>
              <Label htmlFor="name">City Name *</Label>
              <Input
                id="name"
                {...register('name', { required: true })}
                placeholder="e.g., Istanbul, New York"
              />
              {errors.name && <p className="text-sm text-destructive">Name is required</p>}
            </div>

            <div>
              <Label htmlFor="country">Country *</Label>
              <Select
                value={String(selectedCountryId)}
                onValueChange={(value) => setSelectedCountryId(Number(value))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select country" />
                </SelectTrigger>
                <SelectContent>
                  {countries.map((country) => (
                    <SelectItem key={country.id} value={String(country.id)}>
                      {country.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="latitude">Latitude</Label>
                <Input
                  id="latitude"
                  type="number"
                  step="0.000001"
                  {...register('latitude', { valueAsNumber: true })}
                  placeholder="e.g., 41.0082"
                />
              </div>
              <div>
                <Label htmlFor="longitude">Longitude</Label>
                <Input
                  id="longitude"
                  type="number"
                  step="0.000001"
                  {...register('longitude', { valueAsNumber: true })}
                  placeholder="e.g., 28.9784"
                />
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleCloseDialog}>
                Cancel
              </Button>
              <Button type="submit">{editingCity ? 'Update' : 'Create'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CitiesPage;
